/**
 * Map interpretation categories onto a constraint radar (0–1 risk scale).
 * Presentation only — does not re-classify or invent thresholds.
 */
import type { SoilInterpretationDTO } from "@flaha/shared-types";

import {
	categoryToStatus,
	type StatusTone,
} from "./interpretationStatus";

export interface RadarAxis {
	key: string;
	label: string;
	/** 0 = low constraint, 1 = high constraint */
	risk: number;
	tone: StatusTone;
	value: string | null;
}

const AXES: Array<{ field: keyof SoilInterpretationDTO; label: string }> = [
	{ field: "salinityRisk", label: "Salinity" },
	{ field: "sodiumRisk", label: "Sodium" },
	{ field: "phCategory", label: "pH" },
	{ field: "waterHoldingClass", label: "Water" },
	{ field: "cationBalance", label: "Cations" },
	{ field: "cecLevel", label: "CEC" },
];

function toneToRisk(tone: StatusTone): number {
	switch (tone) {
		case "poor":
			return 1;
		case "fair":
			return 0.62;
		case "good":
			return 0.22;
		default:
			return 0.08;
	}
}

/** Build radar axes from interpretation; always returns fixed axis order. */
export function buildConstraintRadarAxes(
	interpretation: SoilInterpretationDTO | null | undefined
): RadarAxis[] {
	return AXES.map(({ field, label }) => {
		const raw = interpretation
			? (interpretation[field] as string | null | undefined)
			: null;
		const value = typeof raw === "string" && raw ? raw : null;
		const status = categoryToStatus(field as string, value);
		return {
			key: field as string,
			label,
			risk: value ? toneToRisk(status.tone) : 0.08,
			tone: value ? status.tone : "neutral",
			value,
		};
	});
}
